import { css } from '@emotion/react';
import type { ApiScanVideoSubmission } from '@kaiizen/kai-pro-api';
import { ScanGroupOptimized } from '@kaiizen/kai-scan-template-system';
import { useCallback, useMemo, useState } from 'react';

import type { ProjectStateLifecycle } from '@/Client/Layout';
import { getGroupTitle, getPartCostValue, getTaskTitle, taskMatchesContractor } from '@/Client/Lib/scopeNavigatorPartPresentersLib';
import { getScopeNotesForLevel, type ScopeNotesLevel } from '@/Client/Lib/scopeNotesLib';
import { formatUsd } from '@/Client/Presenters';
import { useNavigateToScanFocusPanelPage, useScanEditingContextSlice, useScopeAttentionFilters } from '@/Client/State';
import { LAYOUT2 } from '@/Client/Styles';
import { PhotoDropZone } from '@/Client/Widgets';
import { useScanPartPhotoUpload } from '@/Client/Widgets/useScanPartPhotoUpload';

import { styleguideColors as COLORS } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';
import { LeftIcon, RightIcon } from '@kaiizen/kai-shiki/react';
import { FocusedActivityFeed, TaskActivityLog } from '../FocusedActivity';
import { applyGalleryPhotoOperation, PhotoManagerModal } from '../FocusedPhotoGallery';
import type { PhotoManagerScope } from '../FocusedPhotoGallery/photoManagerModel';
import { FOCUS_PANEL_INDENT_PX } from '../FocusPanel/FocusPanelTitlebar';
import {
  resolveVideoToolboxClipReadStatusForMediaStripVideo,
  resolveVideoToolboxPlaybackStartSeconds,
  type ScanAudioAttachScope,
  type VideoToolboxClipReadStatus,
  type VideoToolboxEditorPlaybackContext,
  VideoToolboxPanel,
} from '../VideoToolbox';
import { FocusedContractorOverviewHeader } from './FocusedContractorOverviewHeader';
import { FocusedFullScopeOverviewHeader } from './FocusedFullScopeOverviewHeader';
import { FocusedGroupOverviewHeader } from './FocusedGroupOverviewHeader';
import { getGroupTaskListViewTasks } from './focusedGroupTaskListLib';
import { FocusedGroupTaskListView } from './FocusedGroupTaskListView';
import { FocusedMediaStrip } from './FocusedMediaStrip';
import { getFocusedMediaStripEditorConfig } from './focusedMediaStripLib';
import type { FocusedMediaStripVideoEntry } from './focusedMediaStripLib';
import { FOCUSED_MODULE_TITLE_STYLE } from './focusedModuleStyles';
import { FocusedNotesSection } from './FocusedNotesSection';
import { FocusedTaskEditor } from './FocusedTaskEditor';
import { FocusedTaskOverviewHeader } from './FocusedTaskOverviewHeader';
import { ScopeVideoOriginationEmptyState } from './ScopeVideoOriginationEmptyState';
import { useFocusedMediaStripVideoSubmissions } from './useFocusedMediaStripVideoSubmissions';

/**
 * The drop zone wraps the whole editor, so it has to pass the flex height chain
 * through: the panel's flush body clips overflow, and a plain block div here would
 * grow to the content's full height and take the scrollbar with it.
 */
const FLUSH_DROP_ZONE_STYLE = css`
  label: FOCUSED_FLUSH_DROP_ZONE;
  padding: 0;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
`;

/**
 * Room at the stack's foot for the prev/next float: its 24px offset plus its ~30px
 * height, plus ~50px of clearance so content scrolled to the bottom never sits
 * under it.
 */
const TASK_LIST_BOTTOM_SPACING_STYLE = css`
  label: FOCUSED_TASK_LIST_BOTTOM_SPACING_STYLE;
  flex-shrink: 0;
  height: 104px;
`;

const FOCUSED_SCOPE_EDITOR_STYLE = css`
  label: FOCUSED_SCOPE_EDITOR_STYLE;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
`;

const FOCUSED_SCOPE_MAIN_STYLE = css`
  label: FOCUSED_SCOPE_MAIN_STYLE;
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  position: relative;
  min-height: 0;
  height: 100%;
`;

const SCROLLABLE_CONTENT_STYLE = css`
  label: FOCUSED_SCROLLABLE_CONTENT_STYLE;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  scroll-padding-top: 60px;
`;

const TASKS_SECTION_STYLE = css`
  label: FOCUSED_TASKS_SECTION_STYLE;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

/**
 * Prototype `.pgd-sec-hd` on the scope page's per-group sections: the group's own
 * name is the header — and the way into the group — with its size at the far end.
 */
const SCOPE_GROUP_HEADER_STYLE = css`
  label: FOCUSED_SCOPE_GROUP_HEADER;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`;

const SCOPE_GROUP_NAME_BUTTON_STYLE = css`
  label: FOCUSED_SCOPE_GROUP_NAME_BUTTON;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  color: ${COLORS.ink};
  text-align: left;
  font-family: ${FONT.display};
  font-size: ${pxToRem(15)};
  font-weight: 700;
  letter-spacing: -0.005em;
  line-height: 1.2;

  &:hover > span:first-of-type {
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  &:hover > span:last-of-type {
    color: ${COLORS.ink};
    transform: translateX(2px);
  }
`;

const SCOPE_GROUP_NAME_TEXT_STYLE = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SCOPE_GROUP_GO_STYLE = css`
  font-size: ${pxToRem(15)};
  line-height: 1;
  color: ${COLORS.textTertiary};
  transition: transform 0.12s, color 0.12s;
`;

const SCOPE_GROUP_META_STYLE = css`
  label: FOCUSED_SCOPE_GROUP_META;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(10)};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${COLORS.textTertiary};
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
`;

const SCOPE_GROUP_META_DOT_STYLE = css`
  margin: 0 6px;
  opacity: 0.55;
`;

/**
 * Prototype `.shop-nav-float` — the prev/next pair floating over the panel's foot.
 * Absolute against the editor rather than the viewport, so it stays inside the panel.
 */
const NAV_FLOAT_STYLE = css`
  label: FOCUSED_NAV_FLOAT;
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 60;
  display: flex;
  gap: 6px;
`;

const NAV_BUTTON_STYLE = css`
  label: FOCUSED_NAV_BUTTON;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(10)};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 7px 12px;
  border: 1px solid ${COLORS.stroke};
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(6px);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.10), 0 1px 3px rgba(0, 0, 0, 0.06);
  color: ${COLORS.ink};
  cursor: pointer;
  transition: border-color 0.12s;

  &:hover:not(:disabled) { border-color: ${COLORS.ink}; }

  &:disabled {
    color: ${COLORS.textTertiary};
    cursor: not-allowed;
    background: ${COLORS.sunken};
  }

  svg {
    width: 11px;
    height: 11px;
    stroke: currentColor;
    stroke-width: 1.6;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

/**
 * The wrapper every stacked module sits in — overview header, photos, notes, the task list,
 * activity. One style rather than one per module: they are read as a single column, so the
 * indentation and the gap above each of them is the same thing said once.
 */
const MODULE_SECTION_STYLE = css`
  label: FOCUSED_MODULE_SECTION_WRAPPER_STYLE;
  padding: ${LAYOUT2.spacing2}px ${FOCUS_PANEL_INDENT_PX}px 0;
`;

export type FocusedScopeEditorProps = {
  /**
   * A contractor bucket with no task selected — the Contractor tab's consolidated
   * view. Ignored once `group` or a resolvable `taskId` is present; a contractor
   * is not a real scan part, so it only ever narrows the all-scope task list.
   */
  contractorId?: string;
  filterRegex?: RegExp;
  /** When omitted, `groups` is scanned for scope-navigator tasks (all-scope edit). */
  group?: ScanGroupOptimized;
  groups?: ScanGroupOptimized[];
  /** Opens the scan page's AI walkthrough upload modal (Step 1 origination empty state). */
  onOpenVideoUpload?: () => void;
  /** In-progress analyze jobs for trigger-button affordance on the Step 1 empty state. */
  aiScanVideoActiveCount?: number;
  projectStateLifecycle?: ProjectStateLifecycle;
  /** When set, only this task is shown (within `group`, or resolved across `groups`). */
  taskId?: string;
  /** When true, empty all-scope editor can offer video origination (FocusedEditPanel eligibility). */
  walkthroughUploadEnabled?: boolean;
};

/** Focus-panel editor for all `/edit` focus routes (scope, group, task, and contractor). */
export const FocusedScopeEditor = ({
  contractorId,
  filterRegex,
  group,
  groups = [],
  onOpenVideoUpload,
  aiScanVideoActiveCount = 0,
  projectStateLifecycle = 'edit',
  taskId,
  walkthroughUploadEnabled = false,
}: FocusedScopeEditorProps) => {
  const { activeFilters, evalContext } = useScopeAttentionFilters();
  const {
    scan,
    scanRoot,
    baselineScanRoot,
    scanNotesByScanPartId,
    scanIsEditable,
    scanDataMutators,
    scanHasScopedTasks,
    approvalStatusByTaskId,
    presentTaskIds,
    taskIdToGroupId,
    getContractorForTask,
  } = useScanEditingContextSlice([
    'scan',
    'scanRoot',
    'baselineScanRoot',
    'scanNotesByScanPartId',
    'scanIsEditable',
    'scanDataMutators',
    'scanHasScopedTasks',
    'approvalStatusByTaskId',
    'presentTaskIds',
    'taskIdToGroupId',
    'getContractorForTask',
  ]);

  const resolvedGroup = useMemo(() => {
    if (group) return group;
    if (!taskId) return undefined;
    return groups.find(candidate => candidate.tasks?.some(candidateTask => candidateTask.id === taskId));
  }, [group, groups, taskId]);

  const resolvedTask = useMemo(() => {
    if (!taskId || !resolvedGroup) return undefined;
    return resolvedGroup.tasks?.find(candidateTask => candidateTask.id === taskId);
  }, [resolvedGroup, taskId]);

  /**
   * One config for all three scopes — it already knows the task case, so the photo strip below is
   * the same strip everywhere and the task does not need a copy of its own.
   */
  const mediaStripConfig = useMemo(() => {
    if (!scanRoot) {
      return {
        photoEntries: [],
        videoEntries: [],
        title: 'Group and Task Media',
        emptyLabel: 'No media yet for this scope',
        galleryScope: {},
        mode: 'scope' as const,
      };
    }
    return getFocusedMediaStripEditorConfig({
      group: resolvedGroup,
      groups,
      scanRoot,
      task: resolvedTask,
      taskId: resolvedTask?.id,
    });
  }, [groups, resolvedGroup, resolvedTask, scanRoot]);
  const videoIds = useMemo(
    () => [...new Set(mediaStripConfig.videoEntries.map(entry => entry.videoId))],
    [mediaStripConfig.videoEntries],
  );
  const { submissionsByVideoId } = useFocusedMediaStripVideoSubmissions(videoIds);
  const resolveVideoClipReadStatus = useCallback(
    (video: FocusedMediaStripVideoEntry): VideoToolboxClipReadStatus => resolveVideoToolboxClipReadStatusForMediaStripVideo(scanRoot, video.groupId),
    [scanRoot],
  );
  const [selectedVideoSubmission, setSelectedVideoSubmission] = useState<ApiScanVideoSubmission | undefined>();
  const [videoToolboxOpen, setVideoToolboxOpen] = useState(false);
  const videoToolboxPlaybackContext = useMemo((): VideoToolboxEditorPlaybackContext | undefined => {
    if (!scanRoot) return undefined;
    if (taskId && resolvedGroup && resolvedTask) {
      return { editorScope: 'task', groupId: resolvedGroup.id, taskId: resolvedTask.id };
    }
    const activeGroup = group ?? resolvedGroup;
    if (activeGroup) {
      return { editorScope: 'group', groupId: activeGroup.id };
    }
    return { editorScope: 'scope' };
  }, [group, resolvedGroup, resolvedTask, scanRoot, taskId]);
  const initialPlaybackStartSeconds = useMemo(() => {
    if (!scanRoot || !selectedVideoSubmission || !videoToolboxPlaybackContext) return undefined;
    return resolveVideoToolboxPlaybackStartSeconds(
      selectedVideoSubmission.id,
      scanRoot,
      groups,
      videoToolboxPlaybackContext,
    );
  }, [groups, scanRoot, selectedVideoSubmission, videoToolboxPlaybackContext]);
  const videoToolboxAttachScope = useMemo((): ScanAudioAttachScope => {
    if (videoToolboxPlaybackContext?.editorScope === 'task') {
      return { groupId: videoToolboxPlaybackContext.groupId, taskId: videoToolboxPlaybackContext.taskId };
    }
    if (videoToolboxPlaybackContext?.editorScope === 'group') {
      return { groupId: videoToolboxPlaybackContext.groupId };
    }
    return {};
  }, [videoToolboxPlaybackContext]);
  /**
   * The strip's "View all & add" opens the Photo Manager at the scope the editor is looking at,
   * at any lifecycle (read-only gets browse and detail inside), whether or not that scope holds
   * a photo yet — an empty group or task keeps its section (it is the manager's anchor, exempt
   * from the empty-section filter), so opening from one lands on its add tile. Opening on the
   * whole scan instead would bury that section behind the filter whenever photos exist
   * elsewhere. Hooked here, above the early return — rules of hooks.
   */
  const [photoManagerScope, setPhotoManagerScope] = useState<PhotoManagerScope | undefined>(undefined);
  const [photoManagerPhotoId, setPhotoManagerPhotoId] = useState<string | undefined>(undefined);
  const navigateToScanFocusPanelPage = useNavigateToScanFocusPanelPage();
  /** Notes belong to whatever the panel is looking at: the task, else the group, else the scan. */
  // The same notes the notes drawer lists at this level — a group's include its tasks', the scope's
  // include everything — so the section's count always matches the drawer it opens.
  const levelNotes = useMemo(() => {
    if (!scanRoot) return [];
    const level: ScopeNotesLevel = resolvedGroup && resolvedTask
      ? { kind: 'task', groupId: resolvedGroup.id, taskId: resolvedTask.id }
      : resolvedGroup
      ? { kind: 'group', groupId: resolvedGroup.id }
      : { kind: 'scope' };
    return getScopeNotesForLevel(scanRoot, scanNotesByScanPartId ?? {}, level).map(entry => entry.note);
  }, [scanRoot, scanNotesByScanPartId, resolvedGroup, resolvedTask]);
  /** No group or task in view falls back to the scan root itself — the "Curbside Photo"
   *  scope-level bucket (see galleryPhotoOperations.ts's empty-context target). */
  const {
    inputRef: photoInputRef,
    openFilePicker,
    onSelectFiles,
  } = useScanPartPhotoUpload(resolvedTask ?? resolvedGroup ?? scanRoot, { groupId: resolvedGroup?.id, taskId: resolvedTask?.id });

  /** Task route always shows exactly one task — resolvedTask. */
  const isTaskRoute = !!taskId && !!resolvedGroup;
  const showTaskListView = !isTaskRoute;

  /** A contractor view only applies once nothing more specific (a group or a task) is in view. */
  const resolvedContractorId = !resolvedGroup && !taskId ? contractorId : undefined;

  const groupTaskListSections = useMemo(() => {
    if (!showTaskListView) return [];
    if (resolvedGroup) {
      const tasks = getGroupTaskListViewTasks(resolvedGroup, activeFilters, evalContext, filterRegex);
      return tasks.length ? [{ group: resolvedGroup, tasks }] : [];
    }
    return groups.flatMap(editorGroup => {
      let tasks = getGroupTaskListViewTasks(editorGroup, activeFilters, evalContext, filterRegex);
      if (resolvedContractorId) {
        tasks = tasks.filter(task => taskMatchesContractor(task, resolvedContractorId, { getContractorForTask }));
      }
      if (!tasks.length) return [];
      return [{ group: editorGroup, tasks }];
    });
  }, [activeFilters, evalContext, filterRegex, getContractorForTask, groups, resolvedContractorId, resolvedGroup, showTaskListView]);

  if (!scan) return null;

  const isAllScopeRoute = !group && !taskId;
  const showVideoOriginationEmptyState = !scanHasScopedTasks && isAllScopeRoute && walkthroughUploadEnabled;

  if (showVideoOriginationEmptyState) {
    return (
      <div css={FOCUSED_SCOPE_EDITOR_STYLE}>
        <ScopeVideoOriginationEmptyState
          activeCount={aiScanVideoActiveCount}
          onUploadVideos={onOpenVideoUpload}
        />
      </div>
    );
  }

  const isTaskView = isTaskRoute && !!resolvedGroup && !!resolvedTask;

  /**
   * The overview box: what the panel is looking at, summarised. Every scope draws one, in the same
   * box in the same slot — only what is inside it differs.
   */
  const overviewHeader = (
    <div css={MODULE_SECTION_STYLE}>
      {isTaskView
        ? (
          <FocusedTaskOverviewHeader
            baselineTask={baselineScanRoot?.groupsById[resolvedGroup.id]?.tasksById[resolvedTask.id]}
            group={resolvedGroup}
            groupId={resolvedGroup.id}
            task={resolvedTask}
            taskIsEditRequested={approvalStatusByTaskId[resolvedTask.id] === 'editRequested'}
          />
        )
        : resolvedGroup
        ? (
          <FocusedGroupOverviewHeader
            group={resolvedGroup}
            groups={groups}
            projectStateLifecycle={projectStateLifecycle}
          />
        )
        : resolvedContractorId
        ? (
          <FocusedContractorOverviewHeader
            contractorId={resolvedContractorId}
            groups={groups}
            projectStateLifecycle={projectStateLifecycle}
          />
        )
        : (
          <FocusedFullScopeOverviewHeader
            groups={groups}
            projectStateLifecycle={projectStateLifecycle}
          />
        )}
    </div>
  );

  /**
   * The strip's trailing "+ Add" uploads to the part in view — the task, else the group, else
   * the scan root (Curbside Photo) — so the file input rides along wherever there is one. The
   * count beside the title opens the Progress page, the product's photo surface, at the same
   * scope.
   */
  const canAddPhotos = !!(resolvedTask ?? resolvedGroup ?? scanRoot) && scanIsEditable;
  const photoStrip = (
    <div css={MODULE_SECTION_STYLE}>
      {canAddPhotos
        ? (
          <input
            ref={photoInputRef}
            type={'file'}
            accept={'image/*'}
            multiple
            hidden
            onChange={onSelectFiles}
          />
        )
        : null}
      <FocusedMediaStrip
        title={mediaStripConfig.title}
        photos={mediaStripConfig.photoEntries}
        videos={mediaStripConfig.videoEntries}
        submissionsByVideoId={submissionsByVideoId}
        emptyLabel={mediaStripConfig.emptyLabel}
        actionLabel={canAddPhotos ? mediaStripConfig.actionLabel : undefined}
        showTaskChipLabels={!isTaskView}
        resolveVideoClipReadStatus={resolveVideoClipReadStatus}
        onAction={openFilePicker}
        onOpenPhotoManager={() => {
          setPhotoManagerPhotoId(undefined);
          setPhotoManagerScope(mediaStripConfig.galleryScope);
        }}
        onSelectPhoto={photo => {
          setPhotoManagerPhotoId(photo.photoId);
          setPhotoManagerScope({
            groupId: photo.groupId ?? mediaStripConfig.galleryScope.groupId,
            taskId: photo.taskId ?? mediaStripConfig.galleryScope.taskId,
          });
        }}
        onSelectVideo={(_video: FocusedMediaStripVideoEntry, submission) => {
          if (!submission) return;
          setSelectedVideoSubmission(submission);
          setVideoToolboxOpen(true);
        }}
      />
      <VideoToolboxPanel
        open={videoToolboxOpen}
        submission={selectedVideoSubmission}
        initialPlaybackStartSeconds={initialPlaybackStartSeconds}
        attachScope={videoToolboxAttachScope}
        scopeDisplayNames={{
          groupName: resolvedGroup ? getGroupTitle(resolvedGroup) : undefined,
          taskName: resolvedTask ? getTaskTitle(resolvedTask, 'this task') : undefined,
        }}
        onClose={() => {
          setVideoToolboxOpen(false);
          setSelectedVideoSubmission(undefined);
        }}
      />
    </div>
  );

  // TaskActivityLog's own taskActivity flag picks a task's full log over its status history.
  const activitySection = isTaskView
    ? resolvedTask
      ? (
        <div css={MODULE_SECTION_STYLE}>
          <TaskActivityLog task={resolvedTask} />
        </div>
      )
      : null
    : (
      <div css={MODULE_SECTION_STYLE}>
        <FocusedActivityFeed scopeLevel={resolvedGroup ? 'group' : 'scope'} />
      </div>
    );

  /**
   * Prototype `.shop-nav-float` — the reading order's prev/next, floating over the panel's foot.
   * A task walks every present task on the scan, group to group, straight off the framework's
   * `presentTaskIds` (already in walk order, already computed once) — each stop carrying its own
   * group into the route. A group walks the scan's groups; all-scope needs no walking.
   */
  const orderedTasks = isTaskView
    ? (presentTaskIds ?? []).map(presentTaskId => ({
      groupId: taskIdToGroupId?.[presentTaskId],
      taskId: presentTaskId,
    }))
    : [];
  const taskIndex = isTaskView ? orderedTasks.findIndex(candidate => candidate.taskId === resolvedTask.id) : -1;
  const groupIndex = !isTaskView && resolvedGroup
    ? groups.findIndex(candidate => candidate.id === resolvedGroup.id)
    : -1;
  const navFloat = isTaskView && taskIndex >= 0 && resolvedGroup
    ? (
      <div css={NAV_FLOAT_STYLE} data-testid={'focused-editor-nav'}>
        <button
          type={'button'}
          css={NAV_BUTTON_STYLE}
          disabled={taskIndex <= 0}
          title={'Previous task'}
          onClick={() =>
            navigateToScanFocusPanelPage({
              scanId: scan.id,
              panel: 'edit',
              ...orderedTasks[taskIndex - 1],
            })}
        >
          <LeftIcon aria-hidden={'true'} size={12} />
          {` Previous task`}
        </button>
        <button
          type={'button'}
          css={NAV_BUTTON_STYLE}
          disabled={taskIndex >= orderedTasks.length - 1}
          title={'Next task'}
          onClick={() =>
            navigateToScanFocusPanelPage({
              scanId: scan.id,
              panel: 'edit',
              ...orderedTasks[taskIndex + 1],
            })}
        >
          {`Next task `}
          <RightIcon aria-hidden={'true'} size={12} />
        </button>
      </div>
    )
    : !isTaskView && groupIndex >= 0
    ? (
      <div css={NAV_FLOAT_STYLE} data-testid={'focused-editor-nav'}>
        <button
          type={'button'}
          css={NAV_BUTTON_STYLE}
          disabled={groupIndex <= 0}
          title={groupIndex > 0 ? getGroupTitle(groups[groupIndex - 1]) : 'No previous group'}
          onClick={() =>
            navigateToScanFocusPanelPage({
              scanId: scan.id,
              panel: 'edit',
              groupId: groups[groupIndex - 1].id,
            })}
        >
          <LeftIcon aria-hidden={'true'} size={12} />
          {` Previous group`}
        </button>
        <button
          type={'button'}
          css={NAV_BUTTON_STYLE}
          disabled={groupIndex >= groups.length - 1}
          title={groupIndex < groups.length - 1 ? getGroupTitle(groups[groupIndex + 1]) : 'No next group'}
          onClick={() =>
            navigateToScanFocusPanelPage({
              scanId: scan.id,
              panel: 'edit',
              groupId: groups[groupIndex + 1].id,
            })}
        >
          {`Next group `}
          <RightIcon aria-hidden={'true'} size={12} />
        </button>
      </div>
    )
    : null;

  return (
    <PhotoDropZone
      scanPart={resolvedTask ?? resolvedGroup ?? scanRoot}
      scanPartContext={{ groupId: resolvedGroup?.id, taskId: resolvedTask?.id }}
      dropZoneCss={FLUSH_DROP_ZONE_STYLE}
      label={resolvedTask
        ? `Drop photos into ${getTaskTitle(resolvedTask, 'this task')}`
        : resolvedGroup
        ? `Drop photos into ${getGroupTitle(resolvedGroup)}`
        : 'Drop photos into this scan'}
    >
      <div css={FOCUSED_SCOPE_EDITOR_STYLE}>
        <div css={FOCUSED_SCOPE_MAIN_STYLE}>
          <div css={SCROLLABLE_CONTENT_STYLE}>
            <div css={TASKS_SECTION_STYLE}>
              {overviewHeader}
              {photoStrip}
              <div css={MODULE_SECTION_STYLE}>
                <FocusedNotesSection
                  notes={levelNotes}
                  scanPart={resolvedTask ?? resolvedGroup ?? scanRoot}
                  emptyLabel={isTaskView
                    ? 'No notes yet for this task.'
                    : resolvedGroup
                    ? 'No notes yet in this group.'
                    : 'No notes on this scope yet.'}
                  notesLabel={isTaskView ? 'Task Notes' : resolvedGroup ? 'Group Notes' : 'Scope Notes'}
                />
              </div>
              {isTaskView
                ? (
                  <div css={MODULE_SECTION_STYLE}>
                    <FocusedTaskEditor
                      groupId={resolvedGroup.id}
                      taskId={resolvedTask.id}
                    />
                  </div>
                )
                : groupTaskListSections.flatMap(({ group: listGroup, tasks }) => {
                  if (!tasks.length) return [];
                  return [
                    (
                      <div css={MODULE_SECTION_STYLE} key={listGroup.id}>
                        {!resolvedGroup
                          ? (
                            <div css={SCOPE_GROUP_HEADER_STYLE}>
                              <button
                                type={'button'}
                                css={SCOPE_GROUP_NAME_BUTTON_STYLE}
                                title={`Open ${getGroupTitle(listGroup)}`}
                                data-testid={'focused-scope-group-header'}
                                onClick={() =>
                                  navigateToScanFocusPanelPage({
                                    scanId: scan.id,
                                    panel: 'edit',
                                    groupId: listGroup.id,
                                  })}
                              >
                                <span css={SCOPE_GROUP_NAME_TEXT_STYLE}>{getGroupTitle(listGroup)}</span>
                                <span css={SCOPE_GROUP_GO_STYLE} aria-hidden={'true'}>›</span>
                              </button>
                              <span css={SCOPE_GROUP_META_STYLE}>
                                {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'}
                                <span css={SCOPE_GROUP_META_DOT_STYLE}>·</span>
                                {formatUsd(getPartCostValue(listGroup) ?? 0, false, true)}
                              </span>
                            </div>
                          )
                          : <h3 css={[FOCUSED_MODULE_TITLE_STYLE, { marginBottom: 12 }]}>Tasks in this group</h3>}
                        <FocusedGroupTaskListView group={listGroup} tasks={tasks} />
                      </div>
                    ),
                  ];
                })}
              {activitySection}
              <div css={TASK_LIST_BOTTOM_SPACING_STYLE} />
            </div>
          </div>
          {navFloat}
        </div>
      </div>
      {photoManagerScope && scanRoot
        ? (
          <PhotoManagerModal
            applyPhotoOperation={async operation => {
              await scanDataMutators.setScan(current => applyGalleryPhotoOperation(current, operation));
            }}
            groups={groups}
            initialPhotoId={photoManagerPhotoId}
            onClose={() => {
              setPhotoManagerPhotoId(undefined);
              setPhotoManagerScope(undefined);
            }}
            scanIsEditable={scanIsEditable}
            scanRoot={scanRoot}
            scope={photoManagerScope}
          />
        )
        : null}
    </PhotoDropZone>
  );
};
